import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { safePrint } from "@/utils/safePrint";
import { formatDateDDMonYY } from "@/utils/dateFormat";
import { DateInputDDMonYY } from "@/components/DateInputDDMonYY";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameDay, isSameMonth, subMonths, addMonths } from "date-fns";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  FileText, Plus, Save, Trash2, RefreshCw, FileSpreadsheet,
  Search, Loader2, CheckCircle, Edit, Eye, IndianRupee,
  Calendar, ChevronLeft, ChevronRight, ChevronDown
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const formatCurrency = (val) => {
  if (!val && val !== 0) return '-';
  return '₹' + Math.round(val).toLocaleString('en-IN') + '/-';
};

const formatDateShort = formatDateDDMonYY;

export default function Receipts() {
  const { user } = useAuth();
  const getUsername = () => {
    if (user?.username) return user.username;
    try {
      const stored = JSON.parse(localStorage.getItem('user'));
      return stored?.username || 'unknown';
    } catch { return 'unknown'; }
  };
  const [receipts, setReceipts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receiptForm, setReceiptForm] = useState({ date: format(new Date(), 'yyyy-MM-dd'), amount: '', credit_account: '', debit_account: '', reference: '', narration: '', order_numbers: '', dispatch_numbers: '' });
  const [savingReceipt, setSavingReceipt] = useState(false);
  const [outstandingSearch, setOutstandingSearch] = useState('');
  const [activeView, setActiveView] = useState('outstanding');
  const [outstandingFilter, setOutstandingFilter] = useState('all'); // 'all', 'adv', 'rdap_reg'
  const [clearingParty, setClearingParty] = useState(null);
  const [dayWiseDate, setDayWiseDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  // Calendar state
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarMode, setCalendarMode] = useState('month');

  // Ledger state
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerParty, setLedgerParty] = useState('');
  const [editingReceiptDate, setEditingReceiptDate] = useState(null);
  const [newReceiptDate, setNewReceiptDate] = useState('');

  // Bill detail dialog
  const [billDetailOpen, setBillDetailOpen] = useState(false);
  const [billDetailOrder, setBillDetailOrder] = useState(null);
  const [loadingBill, setLoadingBill] = useState(false);

  // Day receipts dialog
  const [dayReceiptsOpen, setDayReceiptsOpen] = useState(false);
  const [selectedCalDay, setSelectedCalDay] = useState(null);

  // Ledger search
  const [ledgerSearch, setLedgerSearch] = useState('');

  // Editing receipt order/dispatch numbers
  const [editingReceiptId, setEditingReceiptId] = useState(null);
  const [editOrderNums, setEditOrderNums] = useState('');
  const [editDispatchNums, setEditDispatchNums] = useState('');

  // Ledger extras
  const [showAddTxn, setShowAddTxn] = useState(false);
  const [ledgerFromDate, setLedgerFromDate] = useState('');
  const [ledgerToDate, setLedgerToDate] = useState('');

  // Journal entry in ledger
  const [journalForm, setJournalForm] = useState({ date: format(new Date(), 'yyyy-MM-dd'), amount: '', from_account: '', to_account: '', narration: '', reference: '', order_numbers: '', dispatch_numbers: '' });
  const [savingJournal, setSavingJournal] = useState(false);

  // Receipt edit in ledger
  const [editingReceiptAmount, setEditingReceiptAmount] = useState(null);
  const [newReceiptAmount, setNewReceiptAmount] = useState('');

  // Ledger receipt full edit
  const [ledgerEditReceipt, setLedgerEditReceipt] = useState(null);
  const [ledgerEditForm, setLedgerEditForm] = useState({});

  // Settle dialog for credit/debit clear
  const [settleOpen, setSettleOpen] = useState(false);
  const [settleParty, setSettleParty] = useState(null);
  const [settleAccount, setSettleAccount] = useState('');

  // Follow-up action/status
  const [followupOpen, setFollowupOpen] = useState(false);
  const [followupParty, setFollowupParty] = useState(null);
  const [followupAction, setFollowupAction] = useState('');
  const [followupStatus, setFollowupStatus] = useState('');
  const [savingFollowup, setSavingFollowup] = useState(false);
  const [followupTrail, setFollowupTrail] = useState([]);
  const [editingTrailIdx, setEditingTrailIdx] = useState(null);
  const [editingTrailText, setEditingTrailText] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [receiptsRes, accountsRes, customersRes, outstandingRes] = await Promise.all([
        axios.get(`${API}/receipts`),
        axios.get(`${API}/accounts`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/outstanding`)
      ]);
      setReceipts(receiptsRes.data);
      setAccounts(accountsRes.data);
      setCustomers(customersRes.data);
      setOutstanding(outstandingRes.data);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveReceipt = async () => {
    if (!receiptForm.amount || !receiptForm.credit_account || !receiptForm.debit_account) {
      toast.error("Amount, Party and Account are required");
      return;
    }
    setSavingReceipt(true);
    try {
      await axios.post(`${API}/receipts`, { 
        ...receiptForm, 
        amount: parseFloat(receiptForm.amount),
        order_numbers: receiptForm.order_numbers ? receiptForm.order_numbers.split(',').map(s => s.trim()).filter(Boolean) : [],
        dispatch_numbers: receiptForm.dispatch_numbers ? receiptForm.dispatch_numbers.split(',').map(s => s.trim()).filter(Boolean) : []
      });
      toast.success("Receipt saved");
      setReceiptForm({ date: format(new Date(), 'yyyy-MM-dd'), amount: '', credit_account: '', debit_account: '', reference: '', narration: '', order_numbers: '', dispatch_numbers: '' });
      fetchAll();
    } catch (e) {
      toast.error("Failed to save receipt");
    } finally {
      setSavingReceipt(false);
    }
  };

  const deleteReceipt = async (id) => {
    if (!window.confirm("Delete this receipt?")) return;
    try {
      await axios.delete(`${API}/receipts/${id}`);
      toast.success("Receipt deleted");
      fetchAll();
    } catch (e) {
      toast.error("Failed to delete receipt");
    }
  };

  // Clear payment with receipt details dialog
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearPartyData, setClearPartyData] = useState(null);
  const [clearForm, setClearForm] = useState({ amount: '', debit_account: 'Bank', reference: '', narration: '', date: '' });

  const openClearDialog = async (party) => {
    setClearPartyData(party);
    setClearForm({
      amount: Math.abs(party.outstanding),
      debit_account: 'Bank',
      reference: '',
      narration: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      order_numbers: '',
      dispatch_numbers: ''
    });
    setClearDialogOpen(true);
  };

  const submitClear = async () => {
    if (!clearPartyData) return;
    setClearingParty(clearPartyData.party);
    setClearDialogOpen(false);
    try {
      const payload = {
        ...clearForm,
        order_numbers: clearForm.order_numbers ? clearForm.order_numbers.split(',').map(s => s.trim()).filter(Boolean) : [],
        dispatch_numbers: clearForm.dispatch_numbers ? clearForm.dispatch_numbers.split(',').map(s => s.trim()).filter(Boolean) : []
      };
      const res = await axios.post(`${API}/clear-payment/${encodeURIComponent(clearPartyData.party)}`, payload);
      toast.success(`${clearPartyData.party}: ${formatCurrency(res.data.amount)} cleared`);
      fetchAll();
    } catch (e) {
      toast.error("Failed to clear payment");
    } finally {
      setClearingParty(null);
    }
  };

  // Google Sheets posting state for day receipts
  const [sheetPostingDate, setSheetPostingDate] = useState(null);
  const [sheetPostedDates, setSheetPostedDates] = useState(new Set());

  // Open settle dialog for choosing account
  const openSettleDialog = (party) => {
    setSettleParty(party);
    setSettleAccount('');
    setSettleOpen(true);
  };

  // Settle credit balance to selected account
  const settleCredit = async () => {
    if (!settleAccount || !settleParty) return;
    setClearingParty(settleParty.party);
    setSettleOpen(false);
    try {
      const amount = Math.abs(settleParty.outstanding);
      await axios.post(`${API}/receipts`, {
        date: format(new Date(), 'yyyy-MM-dd'),
        amount: amount,
        credit_account: settleAccount,
        debit_account: settleParty.party,
        narration: `Credit settlement: ${settleParty.party} → ${settleAccount}`
      });
      toast.success(`${settleParty.party}: ${formatCurrency(amount)} settled to ${settleAccount}`);
      fetchAll();
    } catch (e) {
      toast.error("Failed to settle credit");
    } finally {
      setClearingParty(null);
      setSettleParty(null);
    }
  };

  // Open follow-up dialog
  const openFollowup = (party) => {
    setFollowupParty(party);
    setFollowupAction('');
    setFollowupTrail(party.followup_trail || []);
    setFollowupOpen(true);
  };

  // Save follow-up
  const saveFollowup = async () => {
    if (!followupParty) return;
    setSavingFollowup(true);
    try {
      await axios.post(`${API}/followup/${encodeURIComponent(followupParty.party)}`, {
        action: followupAction, user: getUsername()
      });
      toast.success("Follow-up saved");
      setFollowupAction('');
      // Refresh trail
      const res = await axios.get(`${API}/followup/${encodeURIComponent(followupParty.party)}`);
      setFollowupTrail(res.data.trail || []);
      fetchAll();
    } catch (e) {
      toast.error("Failed to save follow-up");
    } finally {
      setSavingFollowup(false);
    }
  };

  // Edit trail entry
  const saveTrailEdit = async (idx) => {
    if (!followupParty) return;
    try {
      await axios.put(`${API}/followup/${encodeURIComponent(followupParty.party)}/${idx}`, {
        action: editingTrailText, user: getUsername()
      });
      setEditingTrailIdx(null);
      const res = await axios.get(`${API}/followup/${encodeURIComponent(followupParty.party)}`);
      setFollowupTrail(res.data.trail || []);
      fetchAll();
      toast.success("Entry updated");
    } catch (e) {
      toast.error("Failed to edit");
    }
  };

  // Delete trail entry
  const deleteTrailEntry = async (idx) => {
    if (!followupParty) return;
    try {
      await axios.delete(`${API}/followup/${encodeURIComponent(followupParty.party)}/${idx}`);
      const res = await axios.get(`${API}/followup/${encodeURIComponent(followupParty.party)}`);
      setFollowupTrail(res.data.trail || []);
      fetchAll();
      toast.success("Entry deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  // Clear all follow-ups
  const clearAllFollowups = async () => {
    if (!followupParty) return;
    try {
      await axios.delete(`${API}/followup/${encodeURIComponent(followupParty.party)}`);
      setFollowupTrail([]);
      setFollowupOpen(false);
      fetchAll();
      toast.success("All follow-ups cleared");
    } catch (e) {
      toast.error("Failed to clear");
    }
  };

  // Post day receipts to Google Sheets
  const postReceiptsToSheets = async (date) => {
    setSheetPostingDate(date);
    try {
      await axios.post(`${API}/receipts/post-to-sheets`, { date });
      setSheetPostedDates(prev => new Set([...prev, date]));
      toast.success("Receipts posted to Google Sheets");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post to sheets");
    } finally {
      setSheetPostingDate(null);
    }
  };

  const [postingReceiptId, setPostingReceiptId] = useState(null);
  const postSingleReceiptToSheet = async (receiptId) => {
    setPostingReceiptId(receiptId);
    try {
      const res = await axios.post(`${API}/receipts/${receiptId}/post-to-sheet`);
      toast.success(res.data?.message || "Receipt posted to Google Sheets");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post receipt");
    } finally {
      setPostingReceiptId(null);
    }
  };


  // Edit receipt order/dispatch numbers inline
  const startEditReceiptNums = (receipt) => {
    setEditingReceiptId(receipt.id);
    setEditOrderNums((receipt.order_numbers || []).join(', '));
    setEditDispatchNums((receipt.dispatch_numbers || []).join(', '));
  };

  const saveReceiptNums = async (receiptId) => {
    try {
      const orderNums = editOrderNums ? editOrderNums.split(',').map(s => s.trim()).filter(Boolean) : [];
      const dispatchNums = editDispatchNums ? editDispatchNums.split(',').map(s => s.trim()).filter(Boolean) : [];
      await axios.put(`${API}/receipts/${receiptId}`, { order_numbers: orderNums, dispatch_numbers: dispatchNums });
      setEditingReceiptId(null);
      fetchAll();
      toast.success("Updated");
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  // Auto-fetch order/dispatch numbers for a receipt from party orders
  const autoFetchNums = async (receipt) => {
    try {
      const res = await axios.get(`${API}/party-orders/${encodeURIComponent(receipt.credit_account)}`);
      const orders = res.data || [];
      const orderNums = orders.map(o => o.order_number).filter(Boolean).join(', ');
      const dispatchNums = orders.map(o => o.dispatch_number).filter(Boolean).join(', ');
      setEditOrderNums(orderNums);
      setEditDispatchNums(dispatchNums);
    } catch (e) { toast.error("Could not fetch"); }
  };

  // Print Ledger as PDF
  const printLedger = () => {
    if (!ledgerData || !ledgerParty) return;
    let entries = ledgerData.entries || [];
    // Filter by date range if set
    if (ledgerFromDate) entries = entries.filter(e => e.date >= ledgerFromDate);
    if (ledgerToDate) entries = entries.filter(e => e.date <= ledgerToDate);

    const dateRange = (ledgerFromDate || ledgerToDate) 
      ? `(${ledgerFromDate ? formatDateShort(ledgerFromDate) : 'Start'} to ${ledgerToDate ? formatDateShort(ledgerToDate) : 'Today'})`
      : '';

    const htmlContent = `<html><head><title>Ledger_${ledgerParty}</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 11px; padding: 20px; }
        h2 { margin: 0 0 5px; font-size: 16px; }
        .sub { color: #666; font-size: 11px; margin-bottom: 15px; }
        table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        th, td { border: 1px solid #ccc; padding: 5px 8px; text-align: left; font-size: 10px; }
        th { background: #f3f3f3; font-weight: bold; }
        .right { text-align: right; }
        .bold { font-weight: bold; }
        .red { color: #dc2626; }
        .green { color: #16a34a; }
        .footer { margin-top: 15px; border-top: 1px solid #ccc; padding-top: 10px; }
      </style>
    </head><body>
      <h2>Ledger Statement — ${ledgerParty}</h2>
      <div class="sub">${dateRange}</div>
      <table>
        <thead><tr><th>Date</th><th>Description</th><th>Order No</th><th>Dispatch No</th><th class="right">Debit</th><th class="right">Credit</th><th class="right">Balance</th></tr></thead>
        <tbody>
          ${entries.map(e => `<tr>
            <td>${formatDateShort(e.date)}</td>
            <td>${e.description}</td>
            <td>${e.order_number || '-'}</td>
            <td>${e.dispatch_number || '-'}</td>
            <td class="right">${e.debit > 0 ? formatCurrency(e.debit) : '-'}</td>
            <td class="right green">${e.credit > 0 ? formatCurrency(e.credit) : '-'}</td>
            <td class="right bold ${e.balance > 0 ? 'red' : 'green'}">${formatCurrency(Math.abs(e.balance))}</td>
          </tr>`).join('')}
        </tbody>
      </table>
      <div class="footer">
        <strong>Closing Balance: <span class="${ledgerData.closing_balance > 0 ? 'red' : 'green'}">${formatCurrency(Math.abs(ledgerData.closing_balance))} ${ledgerData.closing_balance > 0 ? '(Due)' : '(Advance)'}</span></strong>
      </div>
    </body></html>`;
    safePrint(htmlContent, `Ledger_${ledgerParty}`);
  };

  const openLedger = async (partyName) => {
    try {
      const res = await axios.get(`${API}/ledger/${encodeURIComponent(partyName)}`);
      setLedgerData(res.data);
      setLedgerParty(partyName);
      setLedgerOpen(true);
    } catch (e) {
      toast.error("Failed to fetch ledger");
    }
  };

  const updateReceiptDate = async (receiptId, newDate) => {
    try {
      await axios.put(`${API}/receipts/${receiptId}`, { date: newDate });
      toast.success("Receipt date updated");
      openLedger(ledgerParty);
      fetchAll();
    } catch (e) {
      toast.error("Failed to update date");
    }
    setEditingReceiptDate(null);
  };

  // Reverse receipt (delete it, moves amount back to outstanding)
  const reverseReceipt = async (receiptId) => {
    if (!window.confirm("Reverse this receipt? Amount will return to outstanding.")) return;
    try {
      await axios.delete(`${API}/receipts/${receiptId}`);
      toast.success("Receipt reversed - amount returned to outstanding");
      openLedger(ledgerParty);
      fetchAll();
    } catch (e) {
      toast.error("Failed to reverse receipt");
    }
  };

  // Edit receipt amount
  const updateReceiptAmount = async (receiptId, newAmount) => {
    if (!newAmount || isNaN(newAmount)) { setEditingReceiptAmount(null); return; }
    try {
      await axios.put(`${API}/receipts/${receiptId}`, { amount: parseFloat(newAmount) });
      toast.success("Receipt amount updated");
      openLedger(ledgerParty);
      fetchAll();
    } catch (e) {
      toast.error("Failed to update amount");
    }
    setEditingReceiptAmount(null);
  };

  // Open full edit for a receipt in ledger
  const openLedgerReceiptEdit = async (receiptId) => {
    try {
      const res = await axios.get(`${API}/receipts`);
      const receipt = (res.data || []).find(r => r.id === receiptId);
      if (receipt) {
        setLedgerEditReceipt(receiptId);
        setLedgerEditForm({
          date: receipt.date || '',
          amount: receipt.amount || 0,
          debit_account: receipt.debit_account || 'Bank',
          narration: receipt.narration || '',
          reference: receipt.reference || '',
          order_numbers: (receipt.order_numbers || []).join(', '),
          dispatch_numbers: (receipt.dispatch_numbers || []).join(', ')
        });
      }
    } catch (e) { toast.error("Failed to load receipt"); }
  };

  const saveLedgerReceiptEdit = async () => {
    if (!ledgerEditReceipt) return;
    try {
      const payload = {
        date: ledgerEditForm.date,
        amount: parseFloat(ledgerEditForm.amount),
        debit_account: ledgerEditForm.debit_account,
        narration: ledgerEditForm.narration,
        reference: ledgerEditForm.reference,
        order_numbers: ledgerEditForm.order_numbers ? ledgerEditForm.order_numbers.split(',').map(s => s.trim()).filter(Boolean) : [],
        dispatch_numbers: ledgerEditForm.dispatch_numbers ? ledgerEditForm.dispatch_numbers.split(',').map(s => s.trim()).filter(Boolean) : []
      };
      await axios.put(`${API}/receipts/${ledgerEditReceipt}`, payload);
      toast.success("Receipt updated");
      setLedgerEditReceipt(null);
      openLedger(ledgerParty);
      fetchAll();
    } catch (e) { toast.error("Failed to update receipt"); }
  };


  // Journal entry (transfer between accounts)
  const saveJournalEntry = async () => {
    const fromAcc = journalForm.from_account || ledgerParty;
    if (!journalForm.amount || !fromAcc || !journalForm.to_account) {
      toast.error("Amount and both accounts are required");
      return;
    }
    setSavingJournal(true);
    try {
      await axios.post(`${API}/receipts`, {
        date: journalForm.date,
        amount: parseFloat(journalForm.amount),
        credit_account: fromAcc,
        debit_account: journalForm.to_account,
        narration: journalForm.narration || `Transfer: ${fromAcc} → ${journalForm.to_account}`,
        reference: journalForm.reference || '',
        order_numbers: journalForm.order_numbers ? journalForm.order_numbers.split(',').map(s => s.trim()).filter(Boolean) : [],
        dispatch_numbers: journalForm.dispatch_numbers ? journalForm.dispatch_numbers.split(',').map(s => s.trim()).filter(Boolean) : []
      });
      toast.success("Transaction saved");
      setJournalForm({ date: format(new Date(), 'yyyy-MM-dd'), amount: '', from_account: '', to_account: '', narration: '', reference: '', order_numbers: '', dispatch_numbers: '' });
      if (ledgerParty) openLedger(ledgerParty);
      fetchAll();
    } catch (e) {
      toast.error("Failed to save transaction");
    } finally {
      setSavingJournal(false);
    }
  };

  const viewBillDetail = async (orderNumber) => {
    setLoadingBill(true);
    setBillDetailOpen(true);
    try {
      const res = await axios.get(`${API}/orders`);
      const orders = res.data?.orders || res.data || [];
      const order = orders.find(o => o.order_number === orderNumber);
      setBillDetailOrder(order || null);
    } catch (e) {
      toast.error("Failed to fetch order");
    } finally {
      setLoadingBill(false);
    }
  };

  // Calendar helpers
  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(calendarDate), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(calendarDate), { weekStartsOn: 1 });
    const days = [];
    let d = start;
    while (d <= end) { days.push(d); d = addDays(d, 1); }
    return days;
  }, [calendarDate]);

  const weekDays = useMemo(() => {
    const start = startOfWeek(calendarDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [calendarDate]);

  const getReceiptsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return receipts.filter(r => r.date === dateStr);
  };

  const filteredOutstanding = outstanding.filter(o => {
    if (o.outstanding === 0) return false;
    if (outstandingSearch && !o.party?.toLowerCase().includes(outstandingSearch.toLowerCase())) return false;
    if (outstandingFilter === 'adv') {
      return (o.payment_terms || []).some(t => t?.toUpperCase() === 'ADV');
    }
    if (outstandingFilter === 'rdap_reg') {
      return (o.payment_terms || []).some(t => ['RDAP', 'REG', 'REGULAR', 'DDAP', 'DDAP RECVD'].includes(t?.toUpperCase()));
    }
    return true;
  });

  const totalOutstandingSum = filteredOutstanding.reduce((s, o) => s + o.outstanding, 0);

  // Compute totals for each filter category (for badge display)
  const allWithBalance = outstanding.filter(o => o.outstanding !== 0);
  const advTotal = allWithBalance.filter(o => (o.payment_terms || []).some(t => t?.toUpperCase() === 'ADV')).reduce((s, o) => s + o.outstanding, 0);
  const rdapRegTotal = allWithBalance.filter(o => (o.payment_terms || []).some(t => ['RDAP', 'REG', 'REGULAR', 'DDAP', 'DDAP RECVD'].includes(t?.toUpperCase()))).reduce((s, o) => s + o.outstanding, 0);
  const allTotal = allWithBalance.reduce((s, o) => s + o.outstanding, 0);

  // Day-wise grouped receipts
  const dayWiseReceipts = useMemo(() => {
    const grouped = {};
    receipts.forEach(r => {
      if (!grouped[r.date]) grouped[r.date] = [];
      grouped[r.date].push(r);
    });
    return Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0]));
  }, [receipts]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="p-4 space-y-4" data-testid="receipts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
          <IndianRupee className="w-5 h-5" /> Receipts & Outstanding
        </h1>
        <div className="flex gap-2">
          <Button size="sm" variant={activeView === 'outstanding' ? 'default' : 'outline'} onClick={() => setActiveView('outstanding')} className="h-8">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Outstanding
          </Button>
          <Button size="sm" variant={activeView === 'ledger' ? 'default' : 'outline'} onClick={() => setActiveView('ledger')} className="h-8">
            <Eye className="w-4 h-4 mr-1" /> Ledger
          </Button>
          <Button size="sm" variant={activeView === 'daywise' ? 'default' : 'outline'} onClick={() => setActiveView('daywise')} className="h-8">
            <FileText className="w-4 h-4 mr-1" /> Day Wise Receipts
          </Button>
          <Button size="sm" variant={activeView === 'calendar' ? 'default' : 'outline'} onClick={() => setActiveView('calendar')} className="h-8">
            <Calendar className="w-4 h-4 mr-1" /> Calendar
          </Button>
          <Button size="sm" variant={activeView === 'new' ? 'default' : 'outline'} onClick={() => setActiveView('new')} className="h-8">
            <Plus className="w-4 h-4 mr-1" /> New Receipt
          </Button>
          <Button size="sm" variant="outline" onClick={fetchAll} className="h-8">
            <RefreshCw className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* ===== OUTSTANDING ===== */}
      {activeView === 'outstanding' && (
        <div className="space-y-3 max-w-6xl">
          {/* Payment Type Filter Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setOutstandingFilter('all')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${outstandingFilter === 'all' ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
              data-testid="filter-all"
            >
              All &nbsp;<span className="font-bold">{formatCurrency(allTotal)}</span>
            </button>
            <button
              onClick={() => setOutstandingFilter('adv')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${outstandingFilter === 'adv' ? 'bg-orange-600 text-white border-orange-600' : 'bg-white text-orange-700 border-orange-300 hover:bg-orange-50'}`}
              data-testid="filter-adv"
            >
              ADV &nbsp;<span className="font-bold">{formatCurrency(advTotal)}</span>
            </button>
            <button
              onClick={() => setOutstandingFilter('rdap_reg')}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${outstandingFilter === 'rdap_reg' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50'}`}
              data-testid="filter-rdap-reg"
            >
              RDAP / REG &nbsp;<span className="font-bold">{formatCurrency(rdapRegTotal)}</span>
            </button>
          </div>
          <div className="flex items-center gap-3">
            <Input placeholder="Search party..." value={outstandingSearch} onChange={e => setOutstandingSearch(e.target.value)} className="h-8 text-xs max-w-xs" data-testid="outstanding-search" />
            <div className="ml-auto flex items-center gap-2">
              <Badge className="text-xs bg-red-100 text-red-800 px-3 py-1" data-testid="total-outstanding-badge">
                {outstandingFilter === 'all' ? 'Total' : outstandingFilter === 'adv' ? 'ADV' : 'RDAP/REG'} Outstanding: {formatCurrency(totalOutstandingSum)}
              </Badge>
              <Badge variant="outline" className="text-xs px-2 py-1">{filteredOutstanding.length} parties</Badge>
            </div>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/50">
                    <TableHead className="text-xs w-[75px]">Last Disp.</TableHead>
                    <TableHead className="text-xs px-1">Party Name</TableHead>
                    <TableHead className="text-xs px-1 w-[70px]">Terms</TableHead>
                    <TableHead className="text-xs text-right px-1 w-[110px] pr-6">Outstanding</TableHead>
                    <TableHead className="text-xs text-center w-[30px] px-0"></TableHead>
                    <TableHead className="text-xs pl-3">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOutstanding.map((o, idx) => {
                    const terms = o.payment_terms || [];
                    const hasAdv = terms.some(t => t?.toUpperCase() === 'ADV');
                    const hasReg = terms.some(t => ['REG', 'REGULAR'].includes(t?.toUpperCase()));
                    const firstBillDate = o.first_bill_date;
                    const lastDispatchDate = o.last_dispatch_date;
                    const daysSinceFirstBill = firstBillDate ? Math.floor((new Date() - new Date(firstBillDate)) / (1000 * 60 * 60 * 24)) : 0;
                    const regOverdue = hasReg && daysSinceFirstBill > 10;
                    const lastTrail = o.followup_trail?.length > 0 ? o.followup_trail[o.followup_trail.length - 1] : null;
                    return (
                      <TableRow key={o.party || idx} className={o.outstanding < 0 ? 'bg-green-50/50' : ''} data-testid={`outstanding-row-${idx}`}>
                        <TableCell className="text-xs text-muted-foreground py-1.5">{formatDateShort(lastDispatchDate || firstBillDate)}</TableCell>
                        <TableCell className="text-xs font-medium py-1.5 px-1">{o.party}</TableCell>
                        <TableCell className="text-xs py-1.5 px-1">
                          {hasAdv && <Badge className="text-[9px] bg-orange-100 text-orange-800 border-orange-300 px-1 py-0">ADV</Badge>}
                          {regOverdue && <Badge className="text-[9px] bg-yellow-100 text-yellow-800 border-yellow-300 px-1 py-0">REG {daysSinceFirstBill}d</Badge>}
                          {!hasAdv && !regOverdue && terms.length > 0 && <span className="text-[10px] text-muted-foreground">{terms[0]}</span>}
                        </TableCell>
                        <TableCell className={`text-xs text-right font-bold py-1.5 px-1 pr-6 ${o.outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {formatCurrency(Math.abs(o.outstanding))}
                          {o.outstanding < 0 && <span className="text-[9px] ml-0.5">(CR)</span>}
                        </TableCell>
                        <TableCell className="text-xs text-center py-1.5 px-0">
                          <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => openLedger(o.party)}>
                            <Eye className="w-3 h-3" />
                          </Button>
                        </TableCell>
                        <TableCell className="text-xs py-1.5 pl-3">
                          {lastTrail ? (
                            <span className="cursor-pointer hover:bg-secondary/30 rounded px-1 py-0.5 inline-block" onClick={() => openFollowup(o)}>
                              <span className="text-[10px] text-orange-600">{lastTrail.action?.length > 30 ? lastTrail.action.slice(0, 30) + '...' : lastTrail.action}</span>
                              <span className="text-[9px] text-muted-foreground ml-1">— {lastTrail.user || 'unknown'}, {lastTrail.timestamp}</span>
                              {o.followup_trail.length > 1 && <Badge variant="outline" className="text-[8px] ml-1 px-1 py-0">{o.followup_trail.length}</Badge>}
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-[10px] cursor-pointer hover:text-blue-600" onClick={() => openFollowup(o)}>+ Add</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredOutstanding.length > 0 && (
                    <TableRow className="bg-secondary/30 font-bold">
                      <TableCell></TableCell>
                      <TableCell className="text-xs px-1">TOTAL ({filteredOutstanding.length})</TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-xs text-right px-1 text-red-600">{formatCurrency(totalOutstandingSum)}</TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== LEDGER SEARCH VIEW ===== */}
      {activeView === 'ledger' && (
        <div className="space-y-3 max-w-5xl">
          <div className="flex items-center gap-2">
            <div className="relative max-w-xs flex-1">
              <Input placeholder="Type party name..." value={ledgerSearch} onChange={e => setLedgerSearch(e.target.value)} className="h-8 text-xs" onKeyDown={e => { if (e.key === 'Enter' && ledgerSearch.trim()) { openLedger(ledgerSearch.trim()); } }} />
              {ledgerSearch.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-50 bg-white border rounded-b-lg shadow-lg max-h-48 overflow-auto">
                  {customers
                    .filter(c => c.name?.toLowerCase().includes(ledgerSearch.toLowerCase()))
                    .slice(0, 10)
                    .map((c, idx) => {
                      const partyOutstanding = outstanding.find(o => o.party === c.name);
                      return (
                        <div key={idx} className="px-3 py-1.5 text-xs hover:bg-secondary/50 cursor-pointer flex justify-between" onClick={() => { setLedgerSearch(''); openLedger(c.name); }}>
                          <span>{c.name}</span>
                          {partyOutstanding ? <span className={partyOutstanding.outstanding > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>{formatCurrency(partyOutstanding.outstanding)}</span> : <span className="text-muted-foreground">-</span>}
                        </div>
                      );
                    })}
                  {customers.filter(c => c.name?.toLowerCase().includes(ledgerSearch.toLowerCase())).length === 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground">No matching party</div>
                  )}
                </div>
              )}
            </div>
            <Button size="sm" className="h-8 text-xs" onClick={() => { if (ledgerSearch.trim()) { openLedger(ledgerSearch.trim()); } }}>
              <Search className="w-3 h-3 mr-1" /> Show Ledger
            </Button>
          </div>
        </div>
      )}

      {/* ===== DAY WISE RECEIPTS ===== */}
      {activeView === 'daywise' && (
        <div className="space-y-3 max-w-6xl">
          <div className="flex items-center gap-2">
            <DateInputDDMonYY value={dayWiseDate} onChange={e => setDayWiseDate(e.target.value)} className="h-8 text-xs w-40" />
          </div>
          {(() => {
            const dayReceipts = receipts.filter(r => r.date === dayWiseDate);
            if (dayReceipts.length === 0) return <Card><CardContent className="text-center text-muted-foreground py-8 text-sm">No receipts on {dayWiseDate}</CardContent></Card>;
            const dayTotal = dayReceipts.reduce((s, r) => s + r.amount, 0);
            const bankTotal = dayReceipts.filter(r => r.debit_account === 'Bank').reduce((s, r) => s + r.amount, 0);
            const mskTotal = dayReceipts.filter(r => r.debit_account === 'Msk').reduce((s, r) => s + r.amount, 0);
            const isPosted = sheetPostedDates.has(dayWiseDate);
            const isPosting = sheetPostingDate === dayWiseDate;
            return (
              <Card>
                <CardHeader className="py-2 px-3 bg-secondary/30 flex-row items-center justify-between">
                  <CardTitle className="text-xs font-bold">{formatDateShort(dayWiseDate)}</CardTitle>
                  <div className="flex gap-3 items-center text-[10px]">
                    {bankTotal > 0 && <Badge variant="outline" className="text-[10px] text-blue-700">Bank: {formatCurrency(bankTotal)}</Badge>}
                    {mskTotal > 0 && <Badge variant="outline" className="text-[10px] text-purple-700">Msk: {formatCurrency(mskTotal)}</Badge>}
                    <Badge className="text-[10px] bg-green-100 text-green-800">Total: {formatCurrency(dayTotal)}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-secondary/50">
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs">Receipt No</TableHead>
                        <TableHead className="text-xs">Party</TableHead>
                        <TableHead className="text-xs text-right">Amount</TableHead>
                        <TableHead className="text-xs">Account</TableHead>
                        <TableHead className="text-xs">Order No</TableHead>
                        <TableHead className="text-xs">Dispatch No</TableHead>
                        <TableHead className="text-xs">Note</TableHead>
                        <TableHead className="text-xs w-16"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dayReceipts.map(r => (
                        <TableRow key={r.id}>
                          <TableCell className="text-xs text-muted-foreground">{formatDateShort(r.date)}</TableCell>
                          <TableCell className="text-xs font-semibold text-blue-700">{r.receipt_number || '-'}</TableCell>
                          <TableCell className="text-xs font-medium">{r.credit_account}</TableCell>
                          <TableCell className="text-xs text-right font-bold text-green-700">{formatCurrency(r.amount)}</TableCell>
                          <TableCell className="text-xs">{r.debit_account}</TableCell>
                          <TableCell className="text-xs">
                            {editingReceiptId === r.id ? (
                              <Input value={editOrderNums} onChange={e => setEditOrderNums(e.target.value)} className="h-6 text-[10px] w-28 px-1" placeholder="Order nos..." />
                            ) : (
                              <span className="cursor-pointer hover:text-blue-600" onClick={() => startEditReceiptNums(r)}>
                                {(Array.isArray(r.order_numbers) ? r.order_numbers : (r.order_numbers ? [r.order_numbers] : [])).length > 0 ? (Array.isArray(r.order_numbers) ? r.order_numbers : [r.order_numbers]).join(', ') : <span className="text-muted-foreground">+ Add</span>}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">
                            {editingReceiptId === r.id ? (
                              <div className="flex items-center gap-1">
                                <Input value={editDispatchNums} onChange={e => setEditDispatchNums(e.target.value)} className="h-6 text-[10px] w-28 px-1" placeholder="Dispatch nos..." />
                                <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-blue-500" onClick={() => autoFetchNums(r)} title="Auto-fetch from orders"><RefreshCw className="w-2.5 h-2.5" /></Button>
                                <Button size="sm" className="h-5 px-1.5 text-[9px]" onClick={() => saveReceiptNums(r.id)}>Save</Button>
                                <Button size="sm" variant="ghost" className="h-5 px-1 text-[9px]" onClick={() => setEditingReceiptId(null)}>X</Button>
                              </div>
                            ) : (
                              <span className="cursor-pointer hover:text-blue-600" onClick={() => startEditReceiptNums(r)}>
                                {(Array.isArray(r.dispatch_numbers) ? r.dispatch_numbers : (r.dispatch_numbers ? [r.dispatch_numbers] : [])).length > 0 ? (Array.isArray(r.dispatch_numbers) ? r.dispatch_numbers : [r.dispatch_numbers]).join(', ') : <span className="text-muted-foreground">+ Add</span>}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{r.narration || r.reference || '-'}</TableCell>
                          <TableCell>
                            <div className="flex gap-0.5">
                              <Button size="sm" variant="ghost" className={`h-5 w-5 p-0 ${r.posted_to_sheet ? 'text-green-600 hover:text-green-800' : 'text-red-500 hover:text-red-700'}`} onClick={() => postSingleReceiptToSheet(r.id)} title={r.posted_to_sheet ? "Posted to Google Sheets" : "Not posted - Click to post"} disabled={postingReceiptId === r.id} data-testid={`post-receipt-${r.id}`}>
                                {postingReceiptId === r.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <FileSpreadsheet className="w-2.5 h-2.5" />}
                              </Button>
                              <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-400" onClick={() => deleteReceipt(r.id)}><Trash2 className="w-2.5 h-2.5" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            );
          })()}
        </div>
      )}

      {/* ===== CALENDAR VIEW ===== */}
      {activeView === 'calendar' && (
        <div className="max-w-5xl space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => setCalendarDate(calendarMode === 'month' ? subMonths(calendarDate, 1) : addDays(calendarDate, -7))}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm font-semibold min-w-[140px] text-center">{format(calendarDate, calendarMode === 'month' ? 'MMMM yyyy' : "'Week of' dd MMM")}</span>
              <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => setCalendarDate(calendarMode === 'month' ? addMonths(calendarDate, 1) : addDays(calendarDate, 7))}><ChevronRight className="w-4 h-4" /></Button>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant={calendarMode === 'month' ? 'default' : 'outline'} onClick={() => setCalendarMode('month')} className="h-7 text-xs">Month</Button>
              <Button size="sm" variant={calendarMode === 'week' ? 'default' : 'outline'} onClick={() => setCalendarMode('week')} className="h-7 text-xs">Week</Button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="border rounded-lg overflow-hidden">
            <div className="grid grid-cols-7 bg-secondary/50">
              {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
                <div key={d} className="text-center text-[10px] font-semibold py-1.5 border-b">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {(calendarMode === 'month' ? calendarDays : weekDays).map((day, idx) => {
                const dayR = getReceiptsForDay(day);
                const total = dayR.reduce((s, r) => s + r.amount, 0);
                const isCurrentMonth = isSameMonth(day, calendarDate);
                const isToday = isSameDay(day, new Date());
                return (
                  <div key={idx} onClick={() => { if (dayR.length > 0) { setSelectedCalDay(day); setDayReceiptsOpen(true); } }}
                    className={`border-b border-r p-1.5 ${calendarMode === 'month' ? 'min-h-[80px]' : 'min-h-[120px]'} ${!isCurrentMonth && calendarMode === 'month' ? 'bg-secondary/20 opacity-40' : ''} ${isToday ? 'bg-blue-50' : ''} ${dayR.length > 0 ? 'cursor-pointer hover:bg-secondary/20' : ''}`}>
                    <div className="text-[10px] font-semibold">{format(day, 'd')}</div>
                    {total > 0 && (
                      <div className="mt-1 space-y-0.5">
                        <div className="text-[9px] font-bold text-green-700 truncate">{formatCurrency(total)}</div>
                        <div className="text-[8px] text-muted-foreground">{dayR.length} receipt{dayR.length > 1 ? 's' : ''}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Period Totals */}
          {(() => {
            const days = calendarMode === 'month' ? calendarDays.filter(d => isSameMonth(d, calendarDate)) : weekDays;
            const periodReceipts = days.flatMap(d => getReceiptsForDay(d));
            const periodTotal = periodReceipts.reduce((s, r) => s + r.amount, 0);
            const bankT = periodReceipts.filter(r => r.debit_account === 'Bank').reduce((s, r) => s + r.amount, 0);
            const mskT = periodReceipts.filter(r => r.debit_account === 'Msk').reduce((s, r) => s + r.amount, 0);
            return (
              <div className="flex gap-3">
                <Card className="flex-1"><CardContent className="p-3 text-center"><div className="text-[10px] text-muted-foreground">Total Receipts</div><div className="text-sm font-bold text-green-700">{formatCurrency(periodTotal)}</div><div className="text-[9px] text-muted-foreground">{periodReceipts.length} entries</div></CardContent></Card>
                <Card className="flex-1"><CardContent className="p-3 text-center"><div className="text-[10px] text-muted-foreground">Bank</div><div className="text-sm font-bold text-blue-700">{formatCurrency(bankT)}</div></CardContent></Card>
                <Card className="flex-1"><CardContent className="p-3 text-center"><div className="text-[10px] text-muted-foreground">Msk</div><div className="text-sm font-bold text-purple-700">{formatCurrency(mskT)}</div></CardContent></Card>
              </div>
            );
          })()}
        </div>
      )}

      {/* ===== NEW RECEIPT ===== */}
      {activeView === 'new' && (
        <Card className="max-w-3xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> New Receipt Entry</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date</Label>
                <DateInputDDMonYY value={receiptForm.date} onChange={e => setReceiptForm({...receiptForm, date: e.target.value})} className="h-8 text-xs" />
              </div>
              <div>
                <Label className="text-xs">Amount (₹)</Label>
                <Input type="number" placeholder="Amount" value={receiptForm.amount} onChange={e => setReceiptForm({...receiptForm, amount: e.target.value})} className="h-8 text-xs" />
              </div>
              <div>
                <Label className="text-xs">Party (Credit)</Label>
                <Select value={receiptForm.credit_account} onValueChange={v => setReceiptForm({...receiptForm, credit_account: v})}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select party" /></SelectTrigger>
                  <SelectContent>
                    {customers.map(c => <SelectItem key={c.id} value={c.name} className="text-xs">{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Account (Debit)</Label>
                <Select value={receiptForm.debit_account} onValueChange={v => setReceiptForm({...receiptForm, debit_account: v})}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Bank / Msk" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bank" className="text-xs">Bank</SelectItem>
                    <SelectItem value="Msk" className="text-xs">Msk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Reference</Label>
                <Input placeholder="Optional" value={receiptForm.reference} onChange={e => setReceiptForm({...receiptForm, reference: e.target.value})} className="h-8 text-xs" />
              </div>
              <div>
                <Label className="text-xs">Narration</Label>
                <Input placeholder="Optional" value={receiptForm.narration} onChange={e => setReceiptForm({...receiptForm, narration: e.target.value})} className="h-8 text-xs" />
              </div>
              <div>
                <Label className="text-xs">Order No(s)</Label>
                <Input placeholder="E.g. E03211, E03212" value={receiptForm.order_numbers} onChange={e => setReceiptForm({...receiptForm, order_numbers: e.target.value})} className="h-8 text-xs" />
              </div>
              <div>
                <Label className="text-xs">Dispatch No(s)</Label>
                <Input placeholder="E.g. D03211, D03212" value={receiptForm.dispatch_numbers} onChange={e => setReceiptForm({...receiptForm, dispatch_numbers: e.target.value})} className="h-8 text-xs" />
              </div>
            </div>
            <Button onClick={saveReceipt} disabled={savingReceipt} className="mt-3 h-8 text-xs">
              {savingReceipt ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save Receipt
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ===== DAY RECEIPTS DIALOG (from calendar click) ===== */}
      <Dialog open={dayReceiptsOpen} onOpenChange={setDayReceiptsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedCalDay && formatDateShort(format(selectedCalDay, 'yyyy-MM-dd'))} - Receipts</DialogTitle>
          </DialogHeader>
          {selectedCalDay && (
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="text-xs">Party</TableHead>
                  <TableHead className="text-xs">Account</TableHead>
                  <TableHead className="text-xs text-right">Amount</TableHead>
                  <TableHead className="text-xs">Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getReceiptsForDay(selectedCalDay).map(r => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs font-medium">{r.credit_account}</TableCell>
                    <TableCell className="text-xs">{r.debit_account}</TableCell>
                    <TableCell className="text-xs text-right font-bold text-green-700">{formatCurrency(r.amount)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.narration || r.reference || '-'}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-secondary/30 font-bold">
                  <TableCell colSpan={2} className="text-xs">TOTAL</TableCell>
                  <TableCell className="text-xs text-right text-green-700">{formatCurrency(getReceiptsForDay(selectedCalDay).reduce((s, r) => s + r.amount, 0))}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== LEDGER DIALOG ===== */}
      <Dialog open={ledgerOpen} onOpenChange={setLedgerOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" /> Ledger - {ledgerParty}
            </DialogTitle>
          </DialogHeader>
          {ledgerData && (
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-secondary/30 rounded-lg">
                <span className="text-sm font-medium">Closing Balance:</span>
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold ${ledgerData.closing_balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {formatCurrency(Math.abs(ledgerData.closing_balance))} {ledgerData.closing_balance > 0 ? '(Due)' : '(Advance)'}
                  </span>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] gap-1" onClick={() => printLedger()} title="Print / Download Ledger PDF">
                    <FileText className="w-3 h-3" /> PDF
                  </Button>
                </div>
              </div>

              {/* Date range filter for ledger print */}
              <div className="flex items-center gap-2">
                <Label className="text-[10px] text-muted-foreground">From:</Label>
                <DateInputDDMonYY value={ledgerFromDate} onChange={e => setLedgerFromDate(e.target.value)} className="h-7 text-xs w-36" />
                <Label className="text-[10px] text-muted-foreground">To:</Label>
                <DateInputDDMonYY value={ledgerToDate} onChange={e => setLedgerToDate(e.target.value)} className="h-7 text-xs w-36" />
              </div>

              {/* Ledger Table */}
              <div className="max-h-[35vh] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/50">
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Description</TableHead>
                      <TableHead className="text-xs">Order No</TableHead>
                      <TableHead className="text-xs">Dispatch No</TableHead>
                      <TableHead className="text-xs text-right">Debit (Sale)</TableHead>
                      <TableHead className="text-xs text-right">Credit (Receipt)</TableHead>
                      <TableHead className="text-xs text-right">Balance</TableHead>
                      <TableHead className="text-xs text-center w-24">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(ledgerData.entries || [])
                      .filter(entry => {
                        if (ledgerFromDate && entry.date < ledgerFromDate) return false;
                        if (ledgerToDate && entry.date > ledgerToDate) return false;
                        return true;
                      })
                      .map((entry, idx) => (
                      <React.Fragment key={`${entry.type}-${entry.date}-${idx}`}>
                      <TableRow className={entry.type === 'receipt' ? 'bg-green-50/50' : ''}>
                        <TableCell className="text-xs">
                          {entry.type === 'receipt' && editingReceiptDate === entry.receipt_id ? (
                            <DateInputDDMonYY value={newReceiptDate} onChange={e => { setNewReceiptDate(e.target.value); updateReceiptDate(entry.receipt_id, e.target.value); }} className="h-6 text-xs w-32" />
                          ) : (
                            <span className={entry.type === 'receipt' ? 'cursor-pointer hover:underline' : ''} onClick={() => { if (entry.type === 'receipt') { setEditingReceiptDate(entry.receipt_id); setNewReceiptDate(entry.date); } }}>
                              {formatDateShort(entry.date)}
                              {entry.type === 'receipt' && <Edit className="w-2.5 h-2.5 inline ml-1 text-muted-foreground" />}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {entry.type === 'sale' ? (
                            <span className="cursor-pointer text-blue-600 hover:underline font-medium" onClick={() => viewBillDetail(entry.reference)}>
                              {entry.description}
                            </span>
                          ) : entry.description}
                        </TableCell>
                        <TableCell className="text-xs font-mono">{entry.order_number || '-'}</TableCell>
                        <TableCell className="text-xs font-mono text-blue-600">{entry.dispatch_number || '-'}</TableCell>
                        <TableCell className="text-xs text-right">{entry.debit > 0 ? formatCurrency(entry.debit) : '-'}</TableCell>
                        <TableCell className="text-xs text-right">
                          {entry.credit > 0 ? (
                            editingReceiptAmount === entry.receipt_id ? (
                              <Input type="number" value={newReceiptAmount} onChange={e => setNewReceiptAmount(e.target.value)} onBlur={() => updateReceiptAmount(entry.receipt_id, newReceiptAmount)} onKeyDown={e => e.key === 'Enter' && updateReceiptAmount(entry.receipt_id, newReceiptAmount)} className="h-6 text-xs w-24 text-right" autoFocus />
                            ) : (
                              <span className="text-green-600 cursor-pointer hover:underline" onClick={() => { setEditingReceiptAmount(entry.receipt_id); setNewReceiptAmount(String(entry.credit)); }}>
                                {formatCurrency(entry.credit)} <Edit className="w-2.5 h-2.5 inline text-muted-foreground" />
                              </span>
                            )
                          ) : '-'}
                        </TableCell>
                        <TableCell className={`text-xs text-right font-medium ${entry.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>{formatCurrency(Math.abs(entry.balance))}</TableCell>
                        <TableCell className="text-xs text-center">
                          {entry.type === 'receipt' && (
                            <div className="flex items-center justify-center gap-1">
                              <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[9px] text-blue-500 hover:text-blue-700 hover:bg-blue-50" onClick={() => openLedgerReceiptEdit(entry.receipt_id)}>
                                <Edit className="w-2.5 h-2.5 mr-0.5" /> Edit
                              </Button>
                              <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[9px] text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => reverseReceipt(entry.receipt_id)}>
                                <Trash2 className="w-2.5 h-2.5 mr-0.5" /> Reverse
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                      {/* Inline edit row for receipt */}
                      {entry.type === 'receipt' && ledgerEditReceipt === entry.receipt_id && (
                        <TableRow className="bg-blue-50/70">
                          <TableCell colSpan={8} className="p-2">
                            <div className="grid grid-cols-7 gap-2 items-end">
                              <div>
                                <Label className="text-[9px] text-muted-foreground">Date</Label>
                                <DateInputDDMonYY value={ledgerEditForm.date} onChange={e => setLedgerEditForm({...ledgerEditForm, date: e.target.value})} className="h-7 text-xs" />
                              </div>
                              <div>
                                <Label className="text-[9px] text-muted-foreground">Amount</Label>
                                <Input type="number" value={ledgerEditForm.amount} onChange={e => setLedgerEditForm({...ledgerEditForm, amount: e.target.value})} className="h-7 text-xs" />
                              </div>
                              <div>
                                <Label className="text-[9px] text-muted-foreground">Account</Label>
                                <Input value={ledgerEditForm.debit_account} onChange={e => setLedgerEditForm({...ledgerEditForm, debit_account: e.target.value})} className="h-7 text-xs" />
                              </div>
                              <div>
                                <Label className="text-[9px] text-muted-foreground">Reference</Label>
                                <Input value={ledgerEditForm.reference} onChange={e => setLedgerEditForm({...ledgerEditForm, reference: e.target.value})} className="h-7 text-xs" placeholder="CHQ/UTR..." />
                              </div>
                              <div>
                                <Label className="text-[9px] text-muted-foreground">Narration</Label>
                                <Input value={ledgerEditForm.narration} onChange={e => setLedgerEditForm({...ledgerEditForm, narration: e.target.value})} className="h-7 text-xs" />
                              </div>
                              <div>
                                <Label className="text-[9px] text-muted-foreground">Order No(s)</Label>
                                <Input value={ledgerEditForm.order_numbers} onChange={e => setLedgerEditForm({...ledgerEditForm, order_numbers: e.target.value})} className="h-7 text-xs" placeholder="E03001, E03002" />
                              </div>
                              <div className="flex gap-1">
                                <Button size="sm" className="h-7 text-xs px-3" onClick={saveLedgerReceiptEdit}>Save</Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => setLedgerEditReceipt(null)}>Cancel</Button>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Transaction / Journal Entry Form - Collapsible */}
              <div className="border rounded-lg bg-amber-50/50">
                <button className="w-full p-2 text-xs font-semibold flex items-center gap-1 hover:bg-amber-100/50 rounded-t-lg" onClick={() => setShowAddTxn(!showAddTxn)}>
                  {showAddTxn ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  <Plus className="w-3 h-3" /> Add Transaction
                </button>
                {showAddTxn && (
                  <div className="p-3 pt-0">
                    <div className="grid grid-cols-5 gap-2">
                      <DateInputDDMonYY value={journalForm.date} onChange={e => setJournalForm({...journalForm, date: e.target.value})} className="h-7 text-xs" />
                      <Input type="number" placeholder="Amount" value={journalForm.amount} onChange={e => setJournalForm({...journalForm, amount: e.target.value})} className="h-7 text-xs" />
                      <Select value={journalForm.from_account || ledgerParty} onValueChange={v => setJournalForm({...journalForm, from_account: v})}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Credit (From)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ledgerParty} className="text-xs font-medium">{ledgerParty} (current)</SelectItem>
                          {customers.filter(c => c.name !== ledgerParty).map(c => <SelectItem key={c.id} value={c.name} className="text-xs">{c.name}</SelectItem>)}
                          <SelectItem value="Bank" className="text-xs">Bank</SelectItem>
                          <SelectItem value="Msk" className="text-xs">Msk</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={journalForm.to_account} onValueChange={v => setJournalForm({...journalForm, to_account: v})}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Debit (To)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Bank" className="text-xs">Bank</SelectItem>
                          <SelectItem value="Msk" className="text-xs">Msk</SelectItem>
                          <SelectItem value="Return Goods" className="text-xs">Return Goods</SelectItem>
                          <SelectItem value="Write Off Balance" className="text-xs">Write Off Balance</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" onClick={() => { if (!journalForm.from_account) setJournalForm(prev => ({...prev, from_account: ledgerParty})); saveJournalEntry(); }} disabled={savingJournal} className="h-7 text-xs">
                        {savingJournal ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
                      </Button>
                    </div>
                    <div className="grid grid-cols-4 gap-2 mt-2">
                      <Input placeholder="Reference (CHQ/UTR)" value={journalForm.reference} onChange={e => setJournalForm({...journalForm, reference: e.target.value})} className="h-7 text-xs" data-testid="journal-reference" />
                      <Input placeholder="Order No(s) e.g. E03211" value={journalForm.order_numbers} onChange={e => setJournalForm({...journalForm, order_numbers: e.target.value})} className="h-7 text-xs" data-testid="journal-order-numbers" />
                      <Input placeholder="Dispatch No(s) e.g. D03211" value={journalForm.dispatch_numbers} onChange={e => setJournalForm({...journalForm, dispatch_numbers: e.target.value})} className="h-7 text-xs" data-testid="journal-dispatch-numbers" />
                      <Input placeholder="Narration (optional)" value={journalForm.narration} onChange={e => setJournalForm({...journalForm, narration: e.target.value})} className="h-7 text-xs" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== BILL DETAIL DIALOG ===== */}
      <Dialog open={billDetailOpen} onOpenChange={setBillDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5" /> Order Summary</DialogTitle>
          </DialogHeader>
          {loadingBill ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : billDetailOrder ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-muted-foreground">Order:</span> <strong>{billDetailOrder.order_number}</strong></div>
                <div><span className="text-muted-foreground">Date:</span> <strong>{formatDateShort(billDetailOrder.order_date)}</strong></div>
                <div><span className="text-muted-foreground">Customer:</span> <strong>{billDetailOrder.customer_name}</strong></div>
                <div><span className="text-muted-foreground">Bill Type:</span> <Badge variant="outline" className="text-[10px]">{billDetailOrder.est_type || 'EST'}</Badge></div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/50">
                    <TableHead className="text-xs">Grade</TableHead>
                    <TableHead className="text-xs">SKU</TableHead>
                    <TableHead className="text-xs text-right">Qty</TableHead>
                    <TableHead className="text-xs text-right">Rate</TableHead>
                    <TableHead className="text-xs text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {billDetailOrder.items?.map((item, idx) => (
                    <TableRow key={`${item.grade}-${item.sku}-${idx}`}>
                      <TableCell className="text-xs">{item.grade}</TableCell>
                      <TableCell className="text-xs">{item.sku}</TableCell>
                      <TableCell className="text-xs text-right">{item.qty}</TableCell>
                      <TableCell className="text-xs text-right">{formatCurrency(item.rate)}</TableCell>
                      <TableCell className="text-xs text-right font-medium">{formatCurrency(item.amount || item.qty * item.rate)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-secondary/30 font-bold">
                    <TableCell colSpan={2} className="text-xs">TOTAL</TableCell>
                    <TableCell className="text-xs text-right">{billDetailOrder.items?.reduce((s, i) => s + (i.qty || 0), 0)}</TableCell>
                    <TableCell></TableCell>
                    <TableCell className="text-xs text-right">{formatCurrency(billDetailOrder.items?.reduce((s, i) => s + (i.amount || i.qty * i.rate || 0), 0))}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              {billDetailOrder.total_discount > 0 && (
                <div className="flex justify-between text-sm p-2 bg-red-50 rounded">
                  <span>Discount:</span>
                  <span className="text-red-600 font-medium">-{formatCurrency(billDetailOrder.total_discount)}</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">Order not found</p>
          )}
        </DialogContent>
      </Dialog>


      {/* ===== CLEAR PAYMENT DIALOG ===== */}
      <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Clear Payment — {clearPartyData?.party}</DialogTitle>
          </DialogHeader>
          {clearPartyData && (
            <div className="space-y-3">
              <div className="text-xs"><span className="text-muted-foreground">Outstanding:</span> <strong className="text-red-600">{formatCurrency(clearPartyData.outstanding)}</strong></div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Amount</Label>
                  <Input type="number" value={clearForm.amount} onChange={e => setClearForm({...clearForm, amount: e.target.value})} className="h-8 text-xs" data-testid="clear-amount" />
                </div>
                <div>
                  <Label className="text-xs">Account</Label>
                  <Select value={clearForm.debit_account} onValueChange={v => setClearForm({...clearForm, debit_account: v})}>
                    <SelectTrigger className="h-8 text-xs" data-testid="clear-account"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bank" className="text-xs">Bank</SelectItem>
                      <SelectItem value="Msk" className="text-xs">Msk</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <DateInputDDMonYY value={clearForm.date} onChange={e => setClearForm({...clearForm, date: e.target.value})} className="h-8 text-xs" />
              </div>
              <div>
                <Label className="text-xs">Reference (Cheque/UTR)</Label>
                <Input value={clearForm.reference} onChange={e => setClearForm({...clearForm, reference: e.target.value})} placeholder="e.g. CHQ 123456" className="h-8 text-xs" data-testid="clear-reference" />
              </div>
              <div>
                <Label className="text-xs">Narration</Label>
                <Input value={clearForm.narration} onChange={e => setClearForm({...clearForm, narration: e.target.value})} placeholder="Payment details..." className="h-8 text-xs" data-testid="clear-narration" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Order No(s)</Label>
                  <Input value={clearForm.order_numbers} onChange={e => setClearForm({...clearForm, order_numbers: e.target.value})} placeholder="e.g. O03001, O03002" className="h-8 text-xs" data-testid="clear-order-numbers" />
                </div>
                <div>
                  <Label className="text-xs">Dispatch No(s)</Label>
                  <Input value={clearForm.dispatch_numbers} onChange={e => setClearForm({...clearForm, dispatch_numbers: e.target.value})} placeholder="e.g. D03001, D03002" className="h-8 text-xs" data-testid="clear-dispatch-numbers" />
                </div>
              </div>
              <Button onClick={submitClear} disabled={!clearForm.amount} className="w-full h-8 text-xs" data-testid="clear-submit-btn">
                <CheckCircle className="w-3 h-3 mr-1" /> Clear {formatCurrency(parseFloat(clearForm.amount) || 0)} to {clearForm.debit_account}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* ===== SETTLE CREDIT DIALOG ===== */}
      <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Settle Credit Balance</DialogTitle>
          </DialogHeader>
          {settleParty && (
            <div className="space-y-3">
              <div className="text-xs space-y-1">
                <div><span className="text-muted-foreground">Party:</span> <strong>{settleParty.party}</strong></div>
                <div><span className="text-muted-foreground">Credit Amount:</span> <strong className="text-green-600">{formatCurrency(Math.abs(settleParty.outstanding))}</strong></div>
              </div>
              <div>
                <Label className="text-xs">Settle to Account</Label>
                <Select value={settleAccount} onValueChange={setSettleAccount}>
                  <SelectTrigger className="h-8 text-xs" data-testid="settle-account-select"><SelectValue placeholder="Select account..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bank" className="text-xs">Bank</SelectItem>
                    <SelectItem value="Msk" className="text-xs">Msk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={settleCredit} disabled={!settleAccount} className="w-full h-8 text-xs" data-testid="settle-confirm-btn">
                <CheckCircle className="w-3 h-3 mr-1" /> Settle {formatCurrency(Math.abs(settleParty.outstanding))} to {settleAccount || '...'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== FOLLOW-UP DIALOG ===== */}
      <Dialog open={followupOpen} onOpenChange={(v) => { setFollowupOpen(v); setEditingTrailIdx(null); }}>
        <DialogContent className="max-w-md max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center justify-between">
              <span>Follow-Up — {followupParty?.party}</span>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold ${followupParty?.outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>{followupParty && formatCurrency(Math.abs(followupParty.outstanding))}{followupParty?.outstanding < 0 ? ' (CR)' : ''}</span>
                {followupTrail.length > 0 && (
                  <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[9px] text-red-500 hover:text-red-700" onClick={clearAllFollowups} title="Clear all">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          {followupParty && (
            <div className="space-y-3">
              {/* Trail thread */}
              {followupTrail.length > 0 ? (
                <div className="max-h-[320px] overflow-auto space-y-2 border rounded-lg p-2 bg-secondary/10">
                  {followupTrail.map((t, i) => (
                    <div key={i} className="border rounded p-2 bg-white group">
                      {editingTrailIdx === i ? (
                        <div className="space-y-1.5">
                          <textarea value={editingTrailText} onChange={e => setEditingTrailText(e.target.value)} className="w-full border rounded p-1.5 text-xs min-h-[36px] resize-none focus:outline-none focus:ring-1 focus:ring-ring" />
                          <div className="flex gap-1">
                            <Button size="sm" className="h-5 px-2 text-[9px]" onClick={() => saveTrailEdit(i)}>Save</Button>
                            <Button size="sm" variant="ghost" className="h-5 px-2 text-[9px]" onClick={() => setEditingTrailIdx(null)}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-xs text-orange-600">{t.action}</p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[9px] text-muted-foreground">
                              <strong>{t.user || 'unknown'}</strong> · {t.timestamp}
                              {t.edited_at && <span className="italic ml-1">(edited by {t.edited_by || t.user}, {t.edited_at})</span>}
                            </span>
                            <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button size="sm" variant="ghost" className="h-4 w-4 p-0 text-muted-foreground hover:text-blue-600" onClick={() => { setEditingTrailIdx(i); setEditingTrailText(t.action); }}>
                                <Edit className="w-2.5 h-2.5" />
                              </Button>
                              <Button size="sm" variant="ghost" className="h-4 w-4 p-0 text-muted-foreground hover:text-red-600" onClick={() => deleteTrailEntry(i)}>
                                <Trash2 className="w-2.5 h-2.5" />
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-xs text-muted-foreground py-4 border rounded-lg bg-secondary/10">No follow-up yet</div>
              )}

              {/* Reply input */}
              <div className="flex gap-2 items-end">
                <textarea placeholder="Write instruction or reply..." value={followupAction} onChange={e => setFollowupAction(e.target.value)} className="flex-1 border rounded-md p-2 text-xs min-h-[40px] max-h-[80px] resize-none focus:outline-none focus:ring-1 focus:ring-ring" data-testid="followup-action-input" />
                <Button onClick={saveFollowup} disabled={savingFollowup || !followupAction.trim()} className="h-9 px-3 text-xs" data-testid="followup-save-btn">
                  {savingFollowup ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
